/**
 * CashflowForecastTable.tsx
 *
 * Unified read-only cashflow forecast table — טבלת תזרים מאוחד.
 *
 * Data sources (all filtered by activeCompanyId):
 *   1. bank_transactions        – ALL entries regardless of status
 *                                  (ignored = bypassed reconciliation, NOT hidden from cashflow)
 *   2. known_actions             – one-off expected entries  (ignored ones excluded)
 *   3. recurring_actions_lines   – repeated / budget entries (ignored ones excluded)
 *
 * Pipeline:
 *   • offsetCalculator  → displayAmount  for Known / Recurring rows
 *   • rolloverUtils     → effectiveDate  (rolled actions move to Today)
 *
 * Sorting default: effectiveDate ASC; same-date tie-break: Bank before others.
 * Running balance is recalculated sequentially after every sort change.
 * Table is READ-ONLY — zero edit / delete buttons.
 *
 * Exports:
 *   • Excel / CSV  – UTF-8 BOM so Hebrew renders correctly in Excel
 *   • PDF          – html2canvas snapshot of a hidden RTL print div
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Loader as Loader2, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, Search, X, RefreshCw, Building2, Calendar, Repeat, TriangleAlert as AlertTriangle, ListFilter as Filter, ChevronDown, Hash, Info, EyeOff, FileSpreadsheet, FileText, AlarmClock } from 'lucide-react';
import { useCompany } from '../../context/CompanyContext';
import { bankTransactionsService } from '../../lib/bankTransactionsService';
import { knownActionsService } from '../../lib/knownActionsService';
import type { KnownAction } from '../../lib/knownActionsService';
import { recurringActionsService } from '../../lib/recurringActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { buildOffsetMap } from '../../lib/offsetCalculator';
import {
  getTodayString,
  buildUnifiedRows,
  getLastBankDate,
} from '../../lib/rolloverUtils';
import type {
  ForecastSourceType,
  ForecastRow,
} from '../../lib/rolloverUtils';
import type { BankTransaction, RecurringActionLine, OffsetCode } from '../../types/database';

// Re-export so any existing importers of ForecastRow from this module still work
export type { ForecastSourceType, ForecastRow };

type SortableField =
  | 'effectiveDate'
  | 'description'
  | 'bankAccountNickname'
  | 'accountingCategoryName'
  | 'cashflowMapping'
  | 'amount'
  | 'runningBalance'
  | 'sourceType';

type SortDirection = 'asc' | 'desc';

// ─── Cashflow mapping labels ──────────────────────────────────────────────────

const CASHFLOW_LABELS: Record<string, string> = {
  current_business:       'שוטף – פעילות עסקית',
  financing_loan:         'מימון – הלוואה',
  intra_group_loan:       'הלוואה תוך-קבוצתית',
  investment:             'השקעה',
  savings:                'חיסכון',
  taxes:                  'מיסים',
  inter_account_transfer: 'העברות בין חשבונות',
  personal:               'אישי',
  other:                  'אחר',
};

function cashflowLabel(mapping: string | null | undefined): string {
  if (!mapping) return '—';
  return CASHFLOW_LABELS[mapping] ?? mapping;
}

// ─── Currency formatter ───────────────────────────────────────────────────────

const ILS = new Intl.NumberFormat('he-IL', {
  style:                 'currency',
  currency:              'ILS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// ─── Source labels (plain text, for export) ───────────────────────────────────

function sourceLabel(s: ForecastSourceType): string {
  return s === 'Bank'       ? 'בנק'      :
         s === 'Known'      ? 'ידוע'     :
         s === 'Recurring'  ? 'קבוע'     :
                              'מגולגל';
}

// ─── Date formatter ───────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

// ─── Source badge ─────────────────────────────────────────────────────────────

interface SourceBadgeProps {
  sourceType:    ForecastSourceType;
  originalDate?: string;
  effectiveDate?: string;
  compact?:      boolean;
}

function SourceBadge({ sourceType, originalDate, effectiveDate, compact }: SourceBadgeProps) {
  type Cfg = { icon: React.ReactNode; label: string; cls: string };
  const cfgs: Record<ForecastSourceType, Cfg> = {
    Bank: {
      icon:  <Building2 className="w-3 h-3 flex-shrink-0" />,
      label: '🏦 בנק',
      cls:   'bg-blue-100 text-blue-800 border border-blue-200',
    },
    Known: {
      icon:  <Calendar className="w-3 h-3 flex-shrink-0" />,
      label: '📅 ידוע',
      cls:   'bg-slate-100 text-slate-700 border border-slate-200',
    },
    Recurring: {
      icon:  <Repeat className="w-3 h-3 flex-shrink-0" />,
      label: '🔁 קבוע',
      cls:   'bg-purple-100 text-purple-800 border border-purple-200',
    },
    RolledOver: {
      icon:  <RefreshCw className="w-3 h-3 flex-shrink-0 animate-spin" />,
      label: '🚨 מגולגל',
      cls:   'bg-red-100 text-red-700 border border-red-300',
    },
  };

  const { icon, label, cls } = cfgs[sourceType];
  const tooltip =
    sourceType === 'RolledOver' && originalDate && effectiveDate
      ? `תאריך מקורי: ${originalDate} ← אפקטיבי: ${effectiveDate}`
      : undefined;

  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1 rounded-full font-semibold whitespace-nowrap ${cls} ${
        compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-[11px]'
      }`}
    >
      {icon}
      {!compact && label}
    </span>
  );
}

// ─── Amount cell ──────────────────────────────────────────────────────────────

function AmountCell({ row }: { row: ForecastRow }) {
  const positive   = row.amount >= 0;
  const colorClass = positive ? 'text-green-600' : 'text-red-600';
  const arrow      = positive ? '▲' : '▼';

  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className={`text-sm font-semibold tabular-nums ${colorClass}`}>
        <span className="text-[10px] mr-0.5 opacity-70">{arrow}</span>
        {ILS.format(Math.abs(row.amount))}
      </span>
      {row.isOffset && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-medium"
          title={`תקציב מקורי: ${ILS.format(row.rawAmount)} | קוזז: ${ILS.format(row.offsetAmount)}`}
        >
          <Info className="w-2.5 h-2.5" />
          קוזז {ILS.format(row.offsetAmount)}
        </span>
      )}
    </div>
  );
}

// ─── Running-balance cell ─────────────────────────────────────────────────────

function BalanceCell({ balance }: { balance: number }) {
  const positive = balance >= 0;
  return (
    <span
      className={`text-sm font-bold tabular-nums ${
        positive ? 'text-emerald-700' : 'text-red-700'
      }`}
    >
      {ILS.format(balance)}
    </span>
  );
}

// ─── Sort-icon helper ─────────────────────────────────────────────────────────

function SortIcon({
  field, current, dir,
}: { field: SortableField; current: SortableField; dir: SortDirection }) {
  if (field !== current) return <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />;
  return dir === 'asc'
    ? <ArrowUp   className="h-3.5 w-3.5 text-primary-600" />
    : <ArrowDown className="h-3.5 w-3.5 text-primary-600" />;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface CashflowForecastTableProps {
  /** Controlled lower bound — when provided the internal date pickers are hidden */
  startDate?: string;
  /** Controlled upper bound — when provided the internal date pickers are hidden */
  endDate?:   string;
}

export function CashflowForecastTable({ startDate, endDate }: CashflowForecastTableProps) {
  const { activeCompanyId } = useCompany();
  const today = getTodayString();

  // ── Raw data ──────────────────────────────────────────────────────────────
  const [bankTxns,       setBankTxns]       = useState<BankTransaction[]>([]);
  const [knownActions,   setKnownActions]   = useState<KnownAction[]>([]);
  const [recurringLines, setRecurringLines] = useState<RecurringActionLine[]>([]);
  const [offsetCodes,    setOffsetCodes]    = useState<OffsetCode[]>([]);

  const [loading,  setLoading]  = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchQuery,    setSearchQuery]    = useState('');
  const [sortField,      setSortField]      = useState<SortableField>('effectiveDate');
  const [sortDir,        setSortDir]        = useState<SortDirection>('asc');
  const [filterSource,   setFilterSource]   = useState<ForecastSourceType | 'All'>('All');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [localStart,     setLocalStart]     = useState(startDate ?? '');
  const [localEnd,       setLocalEnd]       = useState(endDate   ?? '');
  const [exportingPdf,   setExportingPdf]   = useState(false);

  // Sync controlled props → local state when parent changes the range
  useEffect(() => { if (startDate !== undefined) setLocalStart(startDate); }, [startDate]);
  useEffect(() => { if (endDate   !== undefined) setLocalEnd(endDate);     }, [endDate]);
  const filterRef  = useRef<HTMLDivElement>(null);
  const printRef   = useRef<HTMLDivElement>(null);

  // ── Click-outside to close source filter dropdown ────────────────────────
  useEffect(() => {
    if (!showFilterMenu) return;
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterMenu]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [bankRes, knownRes, recurringRes, offsetRes] = await Promise.all([
        bankTransactionsService.getBankTransactions(activeCompanyId),
        knownActionsService.getKnownActions(activeCompanyId),
        recurringActionsService.getRecurringActions(activeCompanyId),
        offsetCodesService.getOffsetCodes(activeCompanyId),
      ]);
      setBankTxns      (bankRes.data       ?? []);
      setKnownActions  (knownRes.data      ?? []);
      setRecurringLines(recurringRes.data  ?? []);
      setOffsetCodes   (offsetRes.data     ?? []);
    } catch (err) {
      console.error('CashflowForecastTable load error:', err);
      setErrorMsg('שגיאה בטעינת נתוני התזרים. נסה לרענן.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Offset map ────────────────────────────────────────────────────────────
  const offsetMap = useMemo(
    () => buildOffsetMap(recurringLines, knownActions, offsetCodes),
    [recurringLines, knownActions, offsetCodes],
  );

  // ── Last bank date (cutoff point) ─────────────────────────────────────────
  const lastBankDate = useMemo(
    () => getLastBankDate(bankTxns),
    [bankTxns],
  );

  // ── Build unified flat rows — "Bank is Truth" pipeline ───────────────────
  const allRows = useMemo<ForecastRow[]>(
    () => buildUnifiedRows({ bankTxns: bankTxns.filter(tx => !tx.is_cc_settlement), knownActions, recurringLines, offsetMap }),
    [bankTxns, knownActions, recurringLines, offsetMap],
  );

  // ── Filter chain ──────────────────────────────────────────────────────────

  const afterDateFilter = useMemo(() => {
    let r = allRows;
    if (localStart) r = r.filter(row => row.effectiveDate >= localStart);
    if (localEnd)   r = r.filter(row => row.effectiveDate <= localEnd);
    return r;
  }, [allRows, localStart, localEnd]);

  const afterSourceFilter = useMemo(() => {
    if (filterSource === 'All') return afterDateFilter;
    return afterDateFilter.filter(r => r.sourceType === filterSource);
  }, [afterDateFilter, filterSource]);

  const afterSearch = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return afterSourceFilter;
    return afterSourceFilter.filter(r =>
      r.description.toLowerCase().includes(q)              ||
      r.bankAccountNickname.toLowerCase().includes(q)      ||
      r.accountingCategoryName.toLowerCase().includes(q)   ||
      cashflowLabel(r.cashflowMapping).toLowerCase().includes(q) ||
      fmtDate(r.effectiveDate).includes(q)                 ||
      Math.abs(r.amount).toFixed(0).includes(q)
    );
  }, [afterSourceFilter, searchQuery]);

  // ── Opening balance: sum of ALL rows strictly BEFORE the date-filter start ─
  const openingBalance = useMemo(() => {
    if (!localStart) return 0;
    return allRows
      .filter(r => r.effectiveDate < localStart)
      .reduce((sum, r) => sum + r.amount, 0);
  }, [allRows, localStart]);

  // ── Sort + running-balance pass ───────────────────────────────────────────

  const sortedRows = useMemo<ForecastRow[]>(() => {
    const rows = [...afterSearch].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'effectiveDate':
          cmp = a.effectiveDate.localeCompare(b.effectiveDate);
          if (cmp === 0) {
            cmp = (a.amount >= 0 ? 0 : 1) - (b.amount >= 0 ? 0 : 1);
          }
          if (cmp === 0) {
            cmp = (a.sourceType === 'Bank' ? 0 : 1) -
                  (b.sourceType === 'Bank' ? 0 : 1);
          }
          break;
        case 'description':
          cmp = a.description.localeCompare(b.description);
          break;
        case 'bankAccountNickname':
          cmp = a.bankAccountNickname.localeCompare(b.bankAccountNickname);
          break;
        case 'accountingCategoryName':
          cmp = a.accountingCategoryName.localeCompare(b.accountingCategoryName);
          break;
        case 'cashflowMapping':
          cmp = cashflowLabel(a.cashflowMapping).localeCompare(cashflowLabel(b.cashflowMapping));
          break;
        case 'amount':
          cmp = a.amount - b.amount;
          break;
        case 'runningBalance':
          cmp = a.runningBalance - b.runningBalance;
          break;
        case 'sourceType':
          cmp = a.sourceType.localeCompare(b.sourceType);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    let running = openingBalance;
    rows.forEach((row, i) => {
      running          += row.amount;
      row.runningBalance = running;
      row.rowNumber      = i + 1;
    });
    return rows;
  }, [afterSearch, sortField, sortDir, openingBalance]);

  // ── PDF rows — always sorted chronologically (ASC) regardless of UI sort ──
  // Built from afterSearch (pre-sort source), NOT from sortedRows.
  // sortedRows mutates row objects in-place (runningBalance, rowNumber),
  // so sharing references with it causes corruption when we re-sort.
  // Starting from afterSearch gives us clean, unmodified objects every time.
  const pdfRows = useMemo<ForecastRow[]>(() => {
    // Deep-clone each row so mutations here never bleed into the UI table
    const rows = afterSearch
      .map(r => ({ ...r }))
      .sort((a, b) => {
        // effectiveDate is always 'YYYY-MM-DD' — lexicographic = chronological
        const dateCmp = a.effectiveDate.localeCompare(b.effectiveDate);
        if (dateCmp !== 0) return dateCmp;
        // Same date: Bank rows first (matches default UI sort tie-break)
        return (a.sourceType === 'Bank' ? 0 : 1) - (b.sourceType === 'Bank' ? 0 : 1);
      });

    // Recompute running balance sequentially in chronological order
    let running = openingBalance;
    rows.forEach((row, i) => {
      running            += row.amount;
      row.runningBalance  = running;
      row.rowNumber       = i + 1;
    });

    return rows;
  }, [afterSearch, openingBalance]);

  // ── Summary stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const income  = sortedRows.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0);
    const expense = sortedRows.filter(r => r.amount < 0).reduce((s, r) => s + r.amount, 0);
    const rolled  = sortedRows.filter(r => r.sourceType === 'RolledOver').length;
    const total   = income + expense;
    return { total, income, expense, rolled, count: sortedRows.length };
  }, [sortedRows]);

  // ── Sort handler ──────────────────────────────────────────────────────────
  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ── Reusable sortable <th> ────────────────────────────────────────────────
  const SortTh = ({
    field, children, className = '',
  }: { field: SortableField; children: React.ReactNode; className?: string }) => (
    <th
      onClick={() => handleSort(field)}
      className={`px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider
        cursor-pointer select-none hover:bg-gray-100 transition-colors ${className}`}
    >
      <div className="flex items-center justify-end gap-1.5">
        {children}
        <SortIcon field={field} current={sortField} dir={sortDir} />
      </div>
    </th>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ── EXCEL / CSV EXPORT ────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  const handleExportExcel = useCallback(() => {
    // CSV with UTF-8 BOM — Excel detects Hebrew correctly
    const BOM = '\uFEFF';

    const headers = [
      '#',
      'מקור',
      'תאריך',
      'תיאור',
      'חשבון בנק',
      'סיווג ח"ח',
      'סיווג תזרים',
      'סכום (₪)',
      'יתרה מצטברת (₪)',
    ];

    const escCsv = (v: string | number): string => {
      const s = String(v ?? '');
      // Wrap in quotes if it contains comma, newline or double-quote
      if (s.includes(',') || s.includes('\n') || s.includes('"')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = sortedRows.map(row => [
      row.rowNumber,
      sourceLabel(row.sourceType),
      fmtDate(row.effectiveDate),
      row.description,
      row.bankAccountNickname,
      row.accountingCategoryName,
      cashflowLabel(row.cashflowMapping),
      row.amount.toFixed(2),
      row.runningBalance.toFixed(2),
    ].map(escCsv).join(','));

    const csv = BOM + headers.map(escCsv).join(',') + '\n' + rows.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `תזרים_${localStart || 'כל_הזמן'}_${localEnd || ''}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [sortedRows, localStart, localEnd]);

  // ══════════════════════════════════════════════════════════════════════════
  // ── PDF EXPORT ────────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  const handleExportPdf = useCallback(async () => {
    if (!printRef.current) return;
    setExportingPdf(true);

    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      // Rows per page — empirically ~22 rows fit on a landscape A4 page at 12px font
      const ROWS_PER_PAGE = 22;

      const captureEl = html2canvas; // suppress unused-var; actual capture uses container below
      void captureEl;
      const el = printRef.current;
      void el;

      const rangeLbl =
        localStart || localEnd
          ? `${localStart ? fmtDate(localStart) : '—'} עד ${localEnd ? fmtDate(localEnd) : '—'}`
          : 'כל הזמן';

      // Temporarily set pdfPageRows to first chunk — we'll re-render via a state trick.
      // Instead, we capture the already-rendered hidden div which always contains ALL rows,
      // but we slice the DOM via a wrapper approach.
      //
      // Simplest correct strategy: render in a temp off-screen container, chunk by rows.
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:0;width:1100px;background:#fff;';
      document.body.appendChild(container);

      const srcCfg: Record<string, { label: string; bg: string; color: string; border: string }> = {
        Bank:       { label: 'בנק',    bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
        Known:      { label: 'ידוע',   bg: '#f1f5f9', color: '#334155', border: '#cbd5e1' },
        Recurring:  { label: 'קבוע',   bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' },
        RolledOver: { label: 'מגולגל', bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
      };

      const buildHeader = () => `
        <div style="text-align:center;margin-bottom:20px;border-bottom:2px solid #3b82f6;padding-bottom:14px">
          <h1 style="font-size:20px;font-weight:bold;color:#1d4ed8;margin:0 0 4px">דוח תזרים מזומנים</h1>
          <p style="font-size:12px;color:#6b7280;margin:0">טווח תאריכים: ${rangeLbl}</p>
        </div>
        <div style="display:flex;gap:14px;margin-bottom:20px;justify-content:flex-end">
          <div style="flex:1;background:${stats.total >= 0 ? '#ecfdf5' : '#fef2f2'};border:1px solid ${stats.total >= 0 ? '#6ee7b7' : '#fca5a5'};border-radius:8px;padding:10px;text-align:right">
            <div style="font-size:10px;color:#6b7280;margin-bottom:3px">יתרה נטו</div>
            <div style="font-size:16px;font-weight:bold;color:${stats.total >= 0 ? '#065f46' : '#991b1b'}">${ILS.format(stats.total)}</div>
          </div>
          <div style="flex:1;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:10px;text-align:right">
            <div style="font-size:10px;color:#6b7280;margin-bottom:3px">▲ סה"כ הכנסות</div>
            <div style="font-size:16px;font-weight:bold;color:#15803d">${ILS.format(stats.income)}</div>
          </div>
          <div style="flex:1;background:#fef2f2;border:1px solid #fca5a5;border-radius:8px;padding:10px;text-align:right">
            <div style="font-size:10px;color:#6b7280;margin-bottom:3px">▼ סה"כ הוצאות</div>
            <div style="font-size:16px;font-weight:bold;color:#b91c1c">${ILS.format(Math.abs(stats.expense))}</div>
          </div>
        </div>`;

      const THEAD = `
        <thead>
          <tr style="background:#1d4ed8;color:#fff">
            ${['מקור','תאריך','תיאור','חשבון בנק','סכום','יתרה מצטברת'].map(h =>
              `<th style="padding:8px 10px;text-align:right;font-weight:bold;font-size:11px;border:1px solid #2563eb">${h}</th>`
            ).join('')}
          </tr>
        </thead>`;

      const buildRowHtml = (row: ForecastRow, idx: number) => {
        const isEven   = idx % 2 === 0;
        const amtColor = row.amount >= 0 ? '#065f46' : '#991b1b';
        const balColor = row.runningBalance >= 0 ? '#065f46' : '#991b1b';
        const src = (srcCfg[row.sourceType] ?? srcCfg['Known'])!;
        return `<tr style="background:${isEven ? '#f8fafc' : '#fff'}">
          <td style="padding:7px 10px;border:1px solid #e5e7eb;white-space:nowrap;text-align:right">
            <span style="display:inline-block;padding:2px 7px;border-radius:9999px;font-size:10px;font-weight:bold;background:${src.bg};color:${src.color};border:1px solid ${src.border}">${src.label}</span>
          </td>
          <td style="padding:7px 10px;border:1px solid #e5e7eb;white-space:nowrap;text-align:right">${fmtDate(row.effectiveDate)}</td>
          <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:right">${row.description}</td>
          <td style="padding:7px 10px;border:1px solid #e5e7eb;white-space:nowrap;text-align:right">${row.bankAccountNickname}</td>
          <td style="padding:7px 10px;border:1px solid #e5e7eb;white-space:nowrap;text-align:right;font-weight:bold;color:${amtColor}">${row.amount >= 0 ? '▲' : '▼'} ${ILS.format(Math.abs(row.amount))}</td>
          <td style="padding:7px 10px;border:1px solid #e5e7eb;white-space:nowrap;text-align:right;font-weight:bold;color:${balColor};background:#eff6ff">${ILS.format(row.runningBalance)}</td>
        </tr>`;
      };

      const TFOOT = `
        <tfoot>
          <tr style="background:#f1f5f9;font-weight:bold">
            <td colspan="4" style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">סה"כ — ${stats.count} שורות</td>
            <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">
              <div style="color:#15803d">▲ ${ILS.format(stats.income)}</div>
              <div style="color:#b91c1c">▼ ${ILS.format(Math.abs(stats.expense))}</div>
            </td>
            <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right;background:#dbeafe">
              <span style="color:${stats.total >= 0 ? '#065f46' : '#991b1b'};font-weight:bold">${ILS.format(stats.total)}</span>
            </td>
          </tr>
        </tfoot>`;

      const pageCount = Math.ceil(pdfRows.length / ROWS_PER_PAGE) || 1;

      for (let page = 0; page < pageCount; page++) {
        const chunk = pdfRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
        const isLast = page === pageCount - 1;

        container.innerHTML = `
          <div dir="rtl" style="font-family:Arial,sans-serif;direction:rtl;padding:24px;background:#fff;color:#111827">
            ${page === 0 ? buildHeader() : ''}
            <table style="width:100%;border-collapse:collapse;font-size:11px;direction:rtl">
              ${THEAD}
              <tbody>${chunk.map((row, idx) => buildRowHtml(row, page * ROWS_PER_PAGE + idx)).join('')}</tbody>
              ${isLast ? TFOOT : ''}
            </table>
            ${isLast ? `<div style="margin-top:16px;text-align:center;font-size:10px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px">הופק בתאריך ${fmtDate(today)} | עמוד ${page + 1} מתוך ${pageCount} | מערכת Axelio</div>` : `<div style="margin-top:8px;text-align:center;font-size:10px;color:#9ca3af">עמוד ${page + 1} מתוך ${pageCount}</div>`}
          </div>`;

        await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        const canvas = await html2canvas(container, {
          scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgH    = (canvas.height / canvas.width) * pageW;

        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pageW, Math.min(imgH, pageH));
      }

      document.body.removeChild(container);

      const fileName = `דוח_תזרים_${localStart || 'כל_הזמן'}_${localEnd || ''}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('שגיאה ביצוא PDF — נסה שוב');
    } finally {
      setExportingPdf(false);
    }
  }, [pdfRows, localStart, localEnd, stats, today]);

  // ──────────────────────────────────────────────────────────────────────────
  // Helper: build the hidden RTL print div content
  // ──────────────────────────────────────────────────────────────────────────

  const dateRangeLabel =
    localStart || localEnd
      ? `${localStart ? fmtDate(localStart) : '—'} עד ${localEnd ? fmtDate(localEnd) : '—'}`
      : 'כל התקופות';

  // ── Guard: no company selected ────────────────────────────────────────────
  if (!activeCompanyId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Building2 className="h-10 w-10 mb-3 text-gray-300" />
        <p className="text-sm">בחר חברה פעילה כדי לצפות בתזרים</p>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3">
        <Loader2 className="h-7 w-7 animate-spin text-primary-500" />
        <span className="text-sm text-gray-500">טוען נתוני תזרים…</span>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (errorMsg) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-600">
        <AlertTriangle className="h-8 w-8" />
        <p className="text-sm">{errorMsg}</p>
        <button onClick={loadAll} className="text-sm text-primary-600 underline">
          נסה שוב
        </button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">

      {/* ════════════════════════════════════════════════════════════════════
          HIDDEN PRINT DIV — captured by html2canvas for PDF export
          (always rendered but visually hidden; shown only during capture)
         ════════════════════════════════════════════════════════════════════ */}
      <div ref={printRef} style={{ display: 'none' }} dir="rtl">
        {/* PDF styles are inline so html2canvas picks them up */}
        <div style={{
          fontFamily: 'Arial, sans-serif',
          direction:  'rtl',
          padding:    '32px',
          background: '#ffffff',
          color:      '#111827',
        }}>
          {/* ── PDF Header ── */}
          <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '2px solid #3b82f6', paddingBottom: '16px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1d4ed8', marginBottom: '4px' }}>
              דוח תזרים מזומנים
            </h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>
              טווח תאריכים: {dateRangeLabel}
            </p>
          </div>

          {/* ── PDF KPI cards ── */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', justifyContent: 'flex-end' }}>
            {/* Net */}
            <div style={{
              flex: 1, background: stats.total >= 0 ? '#ecfdf5' : '#fef2f2',
              border: `1px solid ${stats.total >= 0 ? '#6ee7b7' : '#fca5a5'}`,
              borderRadius: '8px', padding: '12px', textAlign: 'right',
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>יתרה נטו</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: stats.total >= 0 ? '#065f46' : '#991b1b' }}>
                {ILS.format(stats.total)}
              </div>
            </div>
            {/* Income */}
            <div style={{ flex: 1, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '12px', textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>▲ סה"כ הכנסות</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#15803d' }}>
                {ILS.format(stats.income)}
              </div>
            </div>
            {/* Expense */}
            <div style={{ flex: 1, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px', textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>▼ סה"כ הוצאות</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#b91c1c' }}>
                {ILS.format(Math.abs(stats.expense))}
              </div>
            </div>
            {/* Rolled */}
            <div style={{
              flex: 1,
              background: stats.rolled > 0 ? '#fffbeb' : '#f9fafb',
              border: `1px solid ${stats.rolled > 0 ? '#fcd34d' : '#e5e7eb'}`,
              borderRadius: '8px', padding: '12px', textAlign: 'right',
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>🚨 פעולות מגולגלות</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: stats.rolled > 0 ? '#b45309' : '#9ca3af' }}>
                {stats.rolled}
              </div>
            </div>
          </div>

          {/* ── PDF Table (6 columns: מקור | תאריך | תיאור | חשבון בנק | סכום | יתרה מצטברת) ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: 'rtl' }}>
            <thead>
              <tr style={{ background: '#1d4ed8', color: '#ffffff' }}>
                {['מקור', 'תאריך', 'תיאור', 'חשבון בנק', 'סכום', 'יתרה מצטברת'].map(h => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: 'right',
                    fontWeight: 'bold', fontSize: '12px', border: '1px solid #2563eb',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pdfRows.map((row, idx) => {
                const isEven  = idx % 2 === 0;
                const amtColor = row.amount >= 0 ? '#065f46' : '#991b1b';
                const balColor = row.runningBalance >= 0 ? '#065f46' : '#991b1b';

                // Source badge — plain text with inline colour, NO React nodes / icons
                const srcCfg: Record<ForecastSourceType, { label: string; bg: string; color: string; border: string }> = {
                  Bank:       { label: 'בנק',      bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
                  Known:      { label: 'ידוע',     bg: '#f1f5f9', color: '#334155', border: '#cbd5e1' },
                  Recurring:  { label: 'קבוע',     bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' },
                  RolledOver: { label: 'מגולגל',   bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
                };
                const src = srcCfg[row.sourceType];

                return (
                  <tr key={row.key} style={{ background: isEven ? '#f8fafc' : '#ffffff' }}>
                    {/* מקור — rightmost column in RTL */}
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        background: src.bg,
                        color: src.color,
                        border: `1px solid ${src.border}`,
                      }}>
                        {src.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {fmtDate(row.effectiveDate)}
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      {row.description}
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {row.bankAccountNickname}
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 'bold', color: amtColor }}>
                      {row.amount >= 0 ? '▲' : '▼'} {ILS.format(Math.abs(row.amount))}
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 'bold', color: balColor, background: '#eff6ff' }}>
                      {ILS.format(row.runningBalance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Footer totals — colSpan covers מקור + תאריך + תיאור + חשבון בנק = 4 cols */}
            <tfoot>
              <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ padding: '10px 12px', border: '1px solid #e5e7eb', textAlign: 'right' }}>
                  סה"כ — {stats.count} שורות
                </td>
                <td style={{ padding: '10px 12px', border: '1px solid #e5e7eb', textAlign: 'right' }}>
                  <div style={{ color: '#15803d' }}>▲ {ILS.format(stats.income)}</div>
                  <div style={{ color: '#b91c1c' }}>▼ {ILS.format(Math.abs(stats.expense))}</div>
                </td>
                <td style={{ padding: '10px 12px', border: '1px solid #e5e7eb', textAlign: 'right', background: '#dbeafe' }}>
                  <span style={{ color: stats.total >= 0 ? '#065f46' : '#991b1b', fontWeight: 'bold' }}>
                    {ILS.format(stats.total)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* ── PDF Footer ── */}
          <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '10px', color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
            הופק בתאריך {fmtDate(today)} | מערכת Axelio
          </div>
        </div>
      </div>

      {/* ── Last Bank Date banner ──────────────────────────────────────────── */}
      {lastBankDate && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="text-blue-800 font-medium">
            נקודת חיתוך בנקאית:
          </span>
          <span className="font-bold text-blue-900 tabular-nums">
            {fmtDate(lastBankDate)}
          </span>
          <span className="text-blue-600 text-xs">
            — תנועות בנק מוצגות עד תאריך זה; ציפיות קודמות ללא התאמה גוללו להיום
          </span>
        </div>
      )}

      {/* ── KPI summary cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

        {/* Net balance */}
        <div className={`rounded-xl border px-5 py-4 shadow-sm ${
          stats.total >= 0
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <p className="text-xs text-gray-500 mb-1 font-medium">יתרה נטו</p>
          <p className={`text-xl font-bold tabular-nums ${stats.total >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {ILS.format(stats.total)}
          </p>
        </div>

        {/* Total income */}
        <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1 font-medium">▲ סה"כ הכנסות</p>
          <p className="text-xl font-bold text-green-700 tabular-nums">{ILS.format(stats.income)}</p>
        </div>

        {/* Total expenses */}
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1 font-medium">▼ סה"כ הוצאות</p>
          <p className="text-xl font-bold text-red-700 tabular-nums">{ILS.format(Math.abs(stats.expense))}</p>
        </div>

        {/* Rolled-over count */}
        <div className={`rounded-xl border px-5 py-4 shadow-sm ${
          stats.rolled > 0
            ? 'bg-amber-50 border-amber-300'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
            {stats.rolled > 0 && <AlertTriangle className="w-3 h-3 text-amber-500" />}
            🚨 פעולות מגולגלות
          </p>
          <p className={`text-xl font-bold tabular-nums ${stats.rolled > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
            {stats.rolled}
          </p>
        </div>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">

        {/* Free-text search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="חיפוש: תיאור, חשבון, סיווג, סכום…"
            className="w-full pr-9 pl-7 py-2 border border-gray-300 rounded-lg text-sm
              focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Date-range pickers — only shown when parent is not controlling the range */}
        {startDate === undefined && endDate === undefined && (
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="text-gray-500 text-xs">מ:</span>
          <input
            type="date"
            value={localStart}
            onChange={e => setLocalStart(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white
              focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <span className="text-gray-500 text-xs">עד:</span>
          <input
            type="date"
            value={localEnd}
            onChange={e => setLocalEnd(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white
              focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {(localStart || localEnd) && (
            <button
              onClick={() => { setLocalStart(''); setLocalEnd(''); }}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-0.5"
            >
              <X className="h-3 w-3" /> נקה
            </button>
          )}
        </div>
        )}

        {/* Source-type filter dropdown */}
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setShowFilterMenu(v => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg
              text-sm font-medium text-gray-700 bg-white hover:bg-gray-50
              focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <Filter className="h-4 w-4" />
            {filterSource === 'All'        ? 'כל המקורות'  :
             filterSource === 'Bank'       ? '🏦 בנק'       :
             filterSource === 'Known'      ? '📅 ידוע'      :
             filterSource === 'Recurring'  ? '🔁 קבוע'      :
                                            '🚨 מגולגל'}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          {showFilterMenu && (
            <div className="absolute left-0 mt-1 w-44 bg-white border border-gray-200
              rounded-lg shadow-xl z-30 py-1 overflow-hidden">
              {(['All', 'Bank', 'Known', 'Recurring', 'RolledOver'] as const).map(opt => (
                <button
                  key={opt}
                  onClick={() => { setFilterSource(opt); setShowFilterMenu(false); }}
                  className={`w-full text-right px-4 py-2 text-sm transition-colors ${
                    filterSource === opt
                      ? 'bg-primary-50 text-primary-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {opt === 'All'        ? '✦ כל המקורות'  :
                   opt === 'Bank'       ? '🏦 בנק'          :
                   opt === 'Known'      ? '📅 ידוע'         :
                   opt === 'Recurring'  ? '🔁 קבוע'         :
                                        '🚨 מגולגל'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Refresh ── */}
        <button
          onClick={loadAll}
          title="רענן נתונים"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg
            text-sm text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
            focus:ring-primary-500"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="hidden sm:inline text-xs">רענן</span>
        </button>

        {/* ── Export Excel ── */}
        <button
          onClick={handleExportExcel}
          title="ייצא לאקסל (CSV)"
          disabled={sortedRows.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-300 rounded-lg
            text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100
            disabled:opacity-40 disabled:cursor-not-allowed
            focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors"
        >
          <FileSpreadsheet className="h-4 w-4" />
          <span className="hidden sm:inline text-xs">ייצא לאקסל</span>
        </button>

        {/* ── Export PDF ── */}
        <button
          onClick={handleExportPdf}
          title="הורד PDF"
          disabled={sortedRows.length === 0 || exportingPdf}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 rounded-lg
            text-sm font-medium text-rose-700 bg-rose-50 hover:bg-rose-100
            disabled:opacity-40 disabled:cursor-not-allowed
            focus:outline-none focus:ring-2 focus:ring-rose-400 transition-colors"
        >
          {exportingPdf
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <FileText className="h-4 w-4" />
          }
          <span className="hidden sm:inline text-xs">
            {exportingPdf ? 'מייצא…' : 'הורד PDF'}
          </span>
        </button>

        {/* Row counter */}
        <span className="text-xs text-gray-400 whitespace-nowrap ml-auto">
          {stats.count} שורות
        </span>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white">
        {sortedRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <TrendingUp className="h-12 w-12 mb-4 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">אין נתונים להצגה</p>
            <p className="text-xs mt-1">שנה את הפילטרים או טווח התאריכים</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">

              {/* ── thead ── */}
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {/* Row number */}
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 w-10">
                    <Hash className="h-3.5 w-3.5 mx-auto" />
                  </th>

                  {/* Source */}
                  <th
                    onClick={() => handleSort('sourceType')}
                    className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase
                      tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      מקור
                      <SortIcon field="sourceType" current={sortField} dir={sortDir} />
                    </div>
                  </th>

                  <SortTh field="effectiveDate">תאריך</SortTh>
                  <SortTh field="description"   className="min-w-[180px]">תיאור</SortTh>
                  <SortTh field="bankAccountNickname">חשבון בנק</SortTh>
                  <SortTh field="accountingCategoryName">סיווג ח"ח</SortTh>
                  <SortTh field="cashflowMapping">סיווג תזרים</SortTh>
                  <SortTh field="amount">סכום</SortTh>
                  <SortTh field="runningBalance" className="bg-blue-50/60">יתרה מצטברת</SortTh>
                </tr>
              </thead>

              {/* ── tbody ── */}
              <tbody className="divide-y divide-gray-100">
                {sortedRows.map((row, idx) => {
                  const prev          = idx > 0 ? sortedRows[idx - 1]!.runningBalance : 0;
                  const crossesZero   = (prev >= 0 && row.runningBalance < 0) ||
                                        (prev <  0 && row.runningBalance >= 0);
                  const isRolledRow   = row.sourceType === 'RolledOver';
                  const isTodayRow    = row.effectiveDate === today;
                  const isFutureRow   = row.effectiveDate > today;
                  const isPastBankRow = row.sourceType === 'Bank' && row.effectiveDate < today;

                  return (
                    <tr
                      key={row.key}
                      className={[
                        'transition-colors',
                        isRolledRow  ? 'bg-red-50/70 hover:bg-red-100/80'  : '',
                        isFutureRow && !isRolledRow ? 'bg-blue-50/30 hover:bg-blue-50/60' : '',
                        !isRolledRow && !isFutureRow ? 'hover:bg-gray-50' : '',
                        crossesZero  ? 'border-t-2 border-amber-400'        : '',
                      ].join(' ')}
                    >
                      {/* Row number */}
                      <td className="px-3 py-3 text-center text-xs text-gray-400 tabular-nums">
                        {row.rowNumber}
                      </td>

                      {/* Source badge */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <SourceBadge
                          sourceType={row.sourceType}
                          originalDate={fmtDate(row.originalDate)}
                          effectiveDate={fmtDate(row.effectiveDate)}
                        />
                      </td>

                      {/* Effective date */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex flex-col items-end gap-0.5">

                          {/* "Today" badge */}
                          {isTodayRow && (
                            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px]
                              font-bold bg-indigo-100 text-indigo-700 border border-indigo-200 mb-0.5">
                              היום
                            </span>
                          )}

                          <span className={`font-medium text-sm ${
                            isRolledRow   ? 'text-red-700'   :
                            isFutureRow   ? 'text-blue-700'  :
                            isPastBankRow ? 'text-gray-500'  :
                                            'text-gray-900'
                          }`}>
                            {fmtDate(row.effectiveDate)}
                          </span>

                          {/* Strikethrough original date for rolled rows */}
                          {isRolledRow && row.originalDate !== row.effectiveDate && (
                            <span className="text-[10px] text-gray-400 line-through">
                              {fmtDate(row.originalDate)}
                            </span>
                          )}

                          {/* Snooze badge — shown when the row was snoozed to a future date */}
                          {isRolledRow && row.snoozedUntil && row.snoozedUntil >= row.effectiveDate && (
                            <span
                              title={`נדחה עד ${fmtDate(row.snoozedUntil)}`}
                              className="inline-flex items-center gap-0.5 text-[9px] text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-1 py-0 font-semibold"
                            >
                              <AlarmClock className="w-2 h-2" />
                              נדחה
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 text-right max-w-xs">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Subtle hint: bank tx was bypassed by reconciliation */}
                          {row.ignoredByReconciliation && (
                            <span
                              title="תנועה זו סומנה כ'התעלם' בהתאמות בנק — היא כלולה ביתרה המצטברת"
                              className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
                            >
                              <EyeOff className="w-3 h-3" />
                            </span>
                          )}
                          <span className={`font-medium leading-snug ${
                            row.ignoredByReconciliation ? 'text-gray-400' : 'text-gray-900'
                          }`}>
                            {row.description}
                          </span>
                        </div>
                      </td>

                      {/* Bank account */}
                      <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 text-xs">
                        {row.bankAccountNickname}
                      </td>

                      {/* Accounting category */}
                      <td className="px-4 py-3 whitespace-nowrap text-right text-gray-700 text-xs">
                        {row.accountingCategoryName}
                      </td>

                      {/* Cashflow mapping pill */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className="text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                          {cashflowLabel(row.cashflowMapping)}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <AmountCell row={row} />
                      </td>

                      {/* Running balance (pinned column) */}
                      <td className="px-4 py-3 whitespace-nowrap text-right bg-blue-50/40">
                        <BalanceCell balance={row.runningBalance} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* ── tfoot totals ── */}
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                    סה"כ — {stats.count} שורות
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-xs text-green-700 font-semibold">
                        ▲ {ILS.format(stats.income)}
                      </span>
                      <span className="text-xs text-red-700 font-semibold">
                        ▼ {ILS.format(Math.abs(stats.expense))}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right bg-blue-50/40">
                    <BalanceCell balance={stats.total} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-500 pt-1">
        <span className="flex items-center gap-1.5">
          <SourceBadge sourceType="Bank"       compact /> תנועה בנקאית בפועל
        </span>
        <span className="flex items-center gap-1.5">
          <SourceBadge sourceType="Known"      compact /> פעולה ידועה חד-פעמית
        </span>
        <span className="flex items-center gap-1.5">
          <SourceBadge sourceType="Recurring"  compact /> פעולה קבועה (תקציב)
        </span>
        <span className="flex items-center gap-1.5">
          <SourceBadge sourceType="RolledOver" compact /> גולגל להיום (תאריך מקורי בעבר)
        </span>
        <span className="flex items-center gap-1.5 text-amber-600">
          ● שורת מעבר צהובה = יתרה חצתה את האפס
        </span>
        <span className="flex items-center gap-1.5 text-blue-600">
          ● רקע כחול בהיר = פעולה עתידית
        </span>
        <span className="flex items-center gap-1.5 text-indigo-600">
          ● תג "היום" = תאריך אפקטיבי הוא היום
        </span>
        <span className="flex items-center gap-1.5 text-gray-400">
          <EyeOff className="w-3 h-3" /> סומן 'התעלם' בהתאמות בנק — כלול ביתרה
        </span>
      </div>
    </div>
  );
}
